/**
 * The Three.js scene: camera, lights, ground. [P5-scene]
 *
 * A perspective camera looks down at a tilted board, orbits/pans/zooms with damping, and auto-frames
 * as the board grows so play never drifts off screen. Pitch is clamped so the player can never orbit
 * under the table.
 */

import type {
  Box3} from 'three';
import {
  ACESFilmicToneMapping,
  AmbientLight,
  CanvasTexture,
  Color,
  DirectionalLight,
  Fog,
  HemisphereLight,
  Mesh,
  MeshStandardMaterial,
  MOUSE,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  Scene as ThreeScene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export const TILE_SIZE = 1;

const BACKDROP_TOP = '#f4e8cf';
const BACKDROP_MID = '#e2cda4';
const BACKDROP_BOTTOM = '#c3a878';
const HORIZON_FOG = '#d3ba8b';

/** A soft warm-neutral vertical gradient — a lit-table backdrop, not a void. Screen-space, cheap. */
function createBackdropTexture(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 8;
  canvas.height = 512;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, BACKDROP_TOP);
  gradient.addColorStop(0.55, BACKDROP_MID);
  gradient.addColorStop(1, BACKDROP_BOTTOM);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

export type TickListener = (deltaSeconds: number) => void;

/** How long the camera leaves a player's own framing alone before auto-framing may move it again. */
const AUTO_FRAME_YIELD_MS = 12_000;

export class Scene {
  readonly three = new ThreeScene();
  readonly camera: PerspectiveCamera;
  readonly renderer: WebGLRenderer;
  readonly controls: OrbitControls;

  private readonly tickListeners = new Set<TickListener>();
  private lastFrame = performance.now();
  private frameHandle = 0;
  private readonly seenTickErrors = new Set<string>();
  /** Set by the app so a frame error becomes something the player can see, not just a console line. */
  onTickError: ((message: string) => void) | null = null;
  private autoFrameTarget: Box3 | null = null;
  private autoFrameLerp = 0;
  /** When the player last moved the camera themselves. See `requestAutoFrame`. */
  private userMovedCameraAt = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.three.background = createBackdropTexture();
    this.three.fog = new Fog(HORIZON_FOG, 30, 85);

    this.camera = new PerspectiveCamera(42, 1, 0.1, 200);
    this.camera.position.set(0, 9, 9.5);

    this.renderer = new WebGLRenderer({ canvas, antialias: true, alpha: false });
    this.renderer.setClearColor(new Color(HORIZON_FOG));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.outputColorSpace = SRGBColorSpace;

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance = 3;
    this.controls.maxDistance = 40;
    this.controls.maxPolarAngle = Math.PI * 0.47; // never let the player get under the table
    this.controls.minPolarAngle = Math.PI * 0.08;
    this.controls.target.set(0, 0, 0);
    this.controls.update();

    // Pan belongs to the player. OrbitControls already supports it; what it lacked was a way to reach
    // it — right-drag was swallowed by the tile-rotate handler and two-finger touch by the two-finger
    // rotate. Both now require a deliberate *tap*, leaving the drag to the camera. Screen-space panning
    // is the right model for a board on a table: dragging moves the board with the cursor rather than
    // sliding it along the ground plane at an angle you did not ask for.
    this.controls.screenSpacePanning = false;
    this.controls.panSpeed = 0.9;
    // Middle-drag pans too, not dollies: the scroll wheel already zooms, so spending the middle button
    // on a second zoom while pan had no button at all was the wrong trade. Arrow keys pan as well, for
    // anyone who would rather not drag at all.
    this.controls.mouseButtons = { LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.PAN, RIGHT: MOUSE.PAN };
    this.controls.keyPanSpeed = 18;
    this.controls.listenToKeyEvents(window);
    this.controls.addEventListener('start', () => {
      this.userMovedCameraAt = performance.now();
    });

    this.setupLights();
    this.setupGround();
    this.resize();

    window.addEventListener('resize', () => this.resize());
    this.installZoomTrapGuards(canvas);
    this.frameHandle = requestAnimationFrame(this.tick);
  }

  /**
   * Stop a board gesture from zooming the *document*.
   *
   * A trackpad pinch and a ctrl+wheel are the same event to a browser, and neither is covered by the
   * viewport meta tag — so a player reaching to zoom the board could zoom the page instead, and land in
   * a state with scrollbars and a misaligned HUD that only a reload escapes. Tony hit exactly that.
   * Safari's non-standard `gesture*` events need naming separately; they are harmless elsewhere.
   */
  private installZoomTrapGuards(canvas: HTMLCanvasElement): void {
    const block = (event: Event): void => event.preventDefault();
    // Non-passive on purpose: a passive listener cannot preventDefault, which is the entire point.
    canvas.addEventListener('wheel', (event) => { if (event.ctrlKey) event.preventDefault(); }, { passive: false });
    document.addEventListener('gesturestart', block as EventListener);
    document.addEventListener('gesturechange', block as EventListener);
    document.addEventListener('gestureend', block as EventListener);
  }

  private setupLights(): void {
    // Lifted well above the original near-black scene so field green reads as green at a glance —
    // see the lane-polish brief: "the board should be clearly legible on a normal laptop at normal
    // brightness." Tuned together with ACESFilmicToneMapping's exposure above, not in isolation.
    const hemi = new HemisphereLight('#fdf3df', '#7c6947', 1.05);
    this.three.add(hemi);

    const ambient = new AmbientLight('#fff2df', 0.5);
    this.three.add(ambient);

    const key = new DirectionalLight('#fff1d8', 2.3);
    key.position.set(-6, 12, 6);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = -18;
    key.shadow.camera.right = 18;
    key.shadow.camera.top = 18;
    key.shadow.camera.bottom = -18;
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 40;
    key.shadow.bias = -0.0015;
    this.three.add(key);
    this.three.add(key.target);

    // A soft, shadowless fill from roughly the opposite side — low enough not to wash out the key
    // light's modelling, but enough that the [P9-art] tile bevels and masonry relief catch a second
    // highlight on their shaded side instead of going flat black. No shadow map (cheap): this is a
    // fill, not a second key.
    const fill = new DirectionalLight('#dce8ff', 0.4);
    fill.position.set(7, 6, -5);
    this.three.add(fill);
    this.three.add(fill.target);
  }

  private setupGround(): void {
    const geometry = new PlaneGeometry(400, 400);
    const material = new MeshStandardMaterial({ color: '#a5895f', roughness: 1, metalness: 0, fog: true });
    const ground = new Mesh(geometry, material);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.09;
    ground.receiveShadow = true;
    this.three.add(ground);
  }

  onTick(listener: TickListener): () => void {
    this.tickListeners.add(listener);
    return () => this.tickListeners.delete(listener);
  }

  resize(): void {
    const canvas = this.renderer.domElement;
    const parent = canvas.parentElement ?? document.body;
    const width = parent.clientWidth || window.innerWidth;
    const height = parent.clientHeight || window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  /** Jump the camera and its target together to (x, z) on the ground — used by the mini-map. */
  panTo(x: number, z: number): void {
    const dx = x - this.controls.target.x;
    const dz = z - this.controls.target.z;
    this.controls.target.x += dx;
    this.controls.target.z += dz;
    this.camera.position.x += dx;
    this.camera.position.z += dz;
  }

  /**
   * Smoothly frame `bounds` (world-space) over the next several frames.
   *
   * Deferential to the player: auto-framing exists so a growing board does not wander off screen, not
   * so the camera can undo a deliberate choice. Now that panning is reachable, a request that lands
   * seconds after the player moved the camera themselves is dropped — otherwise every tile placed drags
   * the view back and the new pan control would feel broken.
   */
  requestAutoFrame(bounds: Box3): void {
    if (performance.now() - this.userMovedCameraAt < AUTO_FRAME_YIELD_MS) return;
    this.autoFrameTarget = bounds;
    this.autoFrameLerp = 0;
  }

  private applyAutoFrame(dt: number): void {
    if (!this.autoFrameTarget) return;
    this.autoFrameLerp = Math.min(1, this.autoFrameLerp + dt * 1.4);

    const size = new Vector3();
    this.autoFrameTarget.getSize(size);
    const center = new Vector3();
    this.autoFrameTarget.getCenter(center);

    const radius = Math.max(size.x, size.z, 4) * 0.5 + 3;
    const desiredDistance = radius / Math.tan((this.camera.fov * Math.PI) / 360);
    const clampedDistance = Math.min(this.controls.maxDistance, Math.max(this.controls.minDistance, desiredDistance));

    const targetPos = this.controls.target.clone().lerp(center.setY(0), this.autoFrameLerp * 0.12);
    this.controls.target.copy(targetPos);

    const dir = this.camera.position.clone().sub(this.controls.target).normalize();
    const desired = this.controls.target.clone().add(dir.multiplyScalar(clampedDistance));
    this.camera.position.lerp(desired, this.autoFrameLerp * 0.06);

    if (this.autoFrameLerp >= 1) this.autoFrameTarget = null;
  }

  private tick = (): void => {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;

    // Every stage is isolated, and the next frame is scheduled from `finally`.
    //
    // This loop used to be bare. One listener throwing meant the exception escaped `tick()`, the
    // `requestAnimationFrame` at the bottom never ran, and the app was dead for good: nothing
    // rendered, the camera stopped responding (OrbitControls is driven from here), and the only
    // survivors were DOM handlers firing synchronously — which is exactly what a player sees as
    // "the whole game froze". A render loop must never be one exception away from that.
    //
    // Isolating each listener also stops a single misbehaving overlay from taking the others down.
    try {
      this.applyAutoFrame(dt);
      this.controls.update();
    } catch (error) {
      this.reportTickError('camera', error);
    }

    for (const listener of this.tickListeners) {
      try {
        listener(dt);
      } catch (error) {
        this.reportTickError('listener', error);
      }
    }

    try {
      this.renderer.render(this.three, this.camera);
    } catch (error) {
      this.reportTickError('render', error);
    }

    this.frameHandle = requestAnimationFrame(this.tick);
  };

  /**
   * Report a per-frame failure once per distinct message.
   *
   * Per-frame errors arrive sixty times a second, so logging every one buries the first — the only
   * one with useful context. `onTickError` lets the app surface it to the player instead of leaving
   * them to guess why something stopped moving.
   */
  private reportTickError(stage: string, error: unknown): void {
    const message = `${stage}: ${error instanceof Error ? error.message : String(error)}`;
    if (this.seenTickErrors.has(message)) return;
    this.seenTickErrors.add(message);
    console.error(`[scene] frame error in ${stage} — the loop kept running`, error);
    this.onTickError?.(message);
  }

  dispose(): void {
    cancelAnimationFrame(this.frameHandle);
    this.renderer.dispose();
  }
}
